import { connect } from "react-redux"
import {
  withPropsOnChange,
  compose,
  withProps,
  mapProps,
  defaultProps,
  lifecycle,
} from "recompact"
import map from "lodash/map"
import flow from "lodash/flow"
import get from "lodash/get"
import isEqual from "lodash/isEqual"
import isString from "lodash/isString"
import omit from "lodash/omit"
import pick from "lodash/pick"
import withQueryParams from "src/hocs/withQueryParams"
import workspace from "src/hocs/workspace"
import { prepareHocPluginsApplier } from "src/engine/helpers"
import * as entity from "src/engine/modules/entity"
import { selectors, actions } from ".."

/* This HOC supports domains, built on specification RD-1
Specification: http://confluence.infotech.team/pages/viewpage.action?pageId=14947085

In short, your domain must contains .reducer and .saga/.middlewares properties.
*/
const DEFAULT_PAGE_SIZE = 50

const DECISIVE_PROPS = [
  `engine`,
  `model`,
  `query`,
  `to`,
  `propName`,
  `page`,
  `pageSize`,
  `orderBy`,
  `disabled`,
]

const TEMPORARY_PROPS = [
  `query`,
  `page`,
  `pageSize`,
  `orderBy`,
  `createApi`,
  `to`,
  `dispatch`,
  `disabled`,
]

function parseOrderBy(orderByQuery) {
  return orderByQuery && JSON.parse(orderByQuery)
}

export function stringifyOrderBy(orderBy) {
  return map(orderBy, ({ id, desc }) => `${id} ${desc ? `desc` : `asc`}`).join(
    `,`,
  )
}

export default function(config) {
  const { plugins } = config
  const applyPlugins = prepareHocPluginsApplier(plugins)

  const enhancer = compose(
    defaultProps({
      engine: entity,
    }),
    withProps(config),
    withQueryParams(),
    withPropsOnChange(
      [`queryParams`, `controlledByQuery`, `defaultPageSize`],
      ({
        queryParams,
        controlledByQuery,
        defaultPageSize = DEFAULT_PAGE_SIZE,
      }) => {
        const page = controlledByQuery
          ? parseInt(get(queryParams, `page`) || 1, 10)
          : false
        const pageSize = controlledByQuery
          ? parseInt(get(queryParams, `page-size`) || defaultPageSize, 10)
          : false
        const orderByQuery = controlledByQuery
          ? get(queryParams, `order-by`) || ``
          : false
        const filter = controlledByQuery
          ? get(queryParams, `filter`) || ``
          : false

        let orderBy
        if (isString(orderByQuery)) {
          orderBy = stringifyOrderBy(parseOrderBy(orderByQuery))
        }

        return {
          page,
          pageSize,
          orderBy,
          filter,
        }
      },
    ),

    withPropsOnChange(
      (props, nextProps) =>
        !isEqual(
          pick(props, DECISIVE_PROPS),
          pick(nextProps, DECISIVE_PROPS),
        ) || props.orderBy !== nextProps.orderBy,
      props => {
        const {
          /* Entity engine */
          engine,
          /* Entity model  */
          model,
          /* Query */
          query: userQuery,
          /* Target prop */
          to,
          /* More human friendly `to` synonym */
          propName,
          /* Location query parameters */
          page,
          pageSize,
          orderBy,
          disabled,
        } = props

        if (!engine) {
          throw new Error(`collection enhancer expects engine`)
        }

        if (!model) {
          throw new Error(`collection enhancer expects model`)
        }

        const query = {
          ...userQuery,
        }

        if (page && pageSize) {
          query.offset = (page - 1) * pageSize
          query.limit = pageSize
        }

        if (orderBy) {
          query.orderBy = orderBy
        }

        /* Due to the fact that we lock variables during initialization, the API is not recreated */
        const createApi = dispatch => ({
          /* Ignore read if disabled */
          read: disabled
            ? () => {}
            : flow(
                newQuery =>
                  actions.creators.fetch(engine, model, {
                    ...query,
                    ...newQuery,
                  }),
                dispatch,
              ),
          release: flow(
            () => actions.creators.release(engine, model, query),
            dispatch,
          ),
          involve: flow(
            () => actions.creators.involve(engine, model, query),
            dispatch,
          ),
          sortBy: () => {
            // const parsedQuery = queryString.parse(search)
            // parsedQuery.page = page
            // const nextOrderBy = orderBy || {}
            // nextOrderBy[by] = dir
            // parsedQuery.orderBy = stringifyOrderBy(nextOrderBy)
            // history.push(
            //   `${pathname}?${search}/`,
            // )
          },
        })

        return {
          query,
          page,
          pageSize,
          orderBy,
          createApi,
          to: to || propName,
        }
      },
    ),
    applyPlugins(`initial`),

    /* Get state to perform correct pure connect based on model comparsion */
    connect(
      state => ({
        state,
      }),
      dispatch => ({
        dispatch,
      }),
      null,
      {
        pure: false,
      },
    ),

    /* Update [to] only if modelState or createApi has changed */
    withPropsOnChange(
      (props, nextProps) => {
        const { query } = props
        const nextQuery = nextProps.query

        /* Collection state */
        const modelState = selectors.model(
          props.state,
          props.engine,
          props.model,
        )
        const nextModelState = selectors.model(
          nextProps.state,
          nextProps.engine,
          nextProps.model,
        )

        /* Engine state */
        const engineModelState =
          props.engine && props.engine.selectors.model(props.state, props.model)

        const engineNextModelState = nextProps.engine.selectors.model(
          nextProps.state,
          nextProps.model,
        )

        const collectionModelChanged = modelState !== nextModelState
        const engineModelChanged = engineModelState !== engineNextModelState
        const queryChanged = !isEqual(query, nextQuery)

        return collectionModelChanged || engineModelChanged || queryChanged
      },
      props => {
        const {
          to,
          state,
          dispatch,
          engine,
          model,
          query,
          createApi,
          page,
          pageSize,
          orderBy,

          /* Mark that collection should ignore all requests */
          disabled,
        } = props

        /* Mark that collection should work in append mode */
        const cumulative = model.collection && model.collection.cumulative

        /* Select raw data (as array of ids) */
        const rawData = selectors.collection(state, engine, model, query)

        /* Select aggregated data (as array of entities) */
        const data = selectors.data(state, engine, model, query)

        /* Select collection metadata */
        const meta = selectors.meta(state, engine, model, query)

        /* Select collection error */
        const error = selectors.error(state, engine, model, query)

        /* Select collection pending */
        const pending = selectors.pending(state, engine, model, query)

        /* Total pages count */
        const pages = Math.ceil((meta.totalCount || 1) / (pageSize || 1))

        return {
          [to]: {
            engine,
            model,
            query,
            data,
            rawData,
            meta,
            error,
            pending,
            api: createApi(dispatch),
            page,
            pageSize,
            pages,
            totalCount: meta.totalCount ? parseInt(meta.totalCount, 10) : null,
            orderBy,
            cumulative,
            disabled,
          },
        }
      },
    ),
    applyPlugins(`connect`),
    withProps(props => ({
      reinitialize: newProps => {
        if (newProps) {
          const { to } = newProps
          /* If entity item is not virtual, good idea to fetch it
           * right now automatically */
          if (newProps[to].engine && newProps[to].model) {
            /* Read collection */
            newProps[newProps.to].api.read()
            if (
              newProps[newProps.to].model !== props[props.to].model ||
              newProps[newProps.to].engine !== props[props.to].engine ||
              (!newProps[newProps.to].cumulative &&
                !isEqual(newProps[newProps.to].query, props[props.to].query)) ||
              newProps === props
            ) {
              /* Involve collection */
              props[props.to].api.involve()
            }
          }
        }
      },
    })),
    workspace,
    applyPlugins(`workspace`),
    lifecycle({
      componentDidMount() {
        this.props.reinitialize(this.props)
      },
      componentWillUnmount() {
        const { to } = this.props
        const item = this.props[to]

        /* We should release current collection */
        item.api.release()
      },
      // eslint-disable-next-line camelcase
      UNSAFE_componentWillReceiveProps(nextProps) {
        /* Api changes only when configuration has changed, so we can use
         * it as configuration change event */
        if (nextProps.createApi !== this.props.createApi) {
          /* Release previous */
          if (
            nextProps[nextProps.to].model !== this.props[this.props.to].model ||
            nextProps[nextProps.to].engine !==
              this.props[this.props.to].engine ||
            (!nextProps[nextProps.to].cumulative &&
              !isEqual(
                nextProps[nextProps.to].query,
                this.props[this.props.to].query,
              ))
          ) {
            nextProps[nextProps.to].api.release()
          }

          this.props.reinitialize(nextProps)
        }

        if (
          nextProps[nextProps.to].pending !== this.props[this.props.to].pending
        ) {
          if (nextProps[nextProps.to].pending) {
            nextProps.workspace.addLabel(`pending`)
          } else {
            nextProps.workspace.removeLabel(`pending`)
          }
        }
      },
    }),
    applyPlugins(`lifecycle`),
    mapProps(props => omit(props, TEMPORARY_PROPS)),
    applyPlugins(`after`),
  )
  return function CollectionEnhancer(OriginComponent) {
    return enhancer(OriginComponent)
  }
}

/*
На вход transport.js поступает config (example - { controlledByQuery, engine, model, to })
На выход -
  [to]: {
    engine,
    model,
    query,
    data,
    rawData,
    meta,
    error,
    pending,
    api,
    page,
    pageSize,
    pages,
    totalCount,
    orderBy,
    cumulative,
    disabled,
  }
(example - resourceTypes: {
  api: {read: ƒ, release: ƒ, involve: ƒ, sortBy: ƒ}
  cumulative: undefined
  data: [{…}]
  disabled: undefined
  engine: Module {…}
  error: false
  meta: {totalCount: "1"}
  model: Model {name: "resources/types", getRestUrn: ƒ, denormalize: ƒ, schema: Schema}
  orderBy: undefined
  page: false
  pageSize: false
  pages: 1
  pending: false
})

Также через transport.js проходят отдельные пропсы, без которых front падает:

// падает при открытии календаря
calendarQuery: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
dataRectChangeHandler: PropTypes.func,
date: PropTypes.string,
loadCalendarData: PropTypes.func,
scale: PropTypes.string,

// падает при открытии календаря плана
planId: PropTypes.string,
planResourceTypes: PropTypes.object,
eventResources: PropTypes.object,

// падает при открытии карточки события
eventUuid: PropTypes.string,

// падает при редактировании/создании ресурса
  resourceId: PropTypes.string,
},

И Будда занет что еще.....
* */
